import { ImageWrapper } from "../components/ImageWrapper";
import { Tooltip } from "../components/Tooltip";
import { User01 } from "../icons/v2-stroke";
import { getEmojiAndBackgroundFromUrl } from "../lib/avatar/utils";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React, { useState } from "react";
export const AVATAR_SIZES = [
    "xxs",
    "xs",
    "sm",
    "md",
    "lg",
    "xl",
    "2xl",
    "auto",
];
export const AVATAR_VARIANTS = ["default", "clickable", "disabled"];
const avatarVariants = cva("flex flex-shrink-0 items-center justify-center overflow-hidden", {
    variants: {
        size: {
            xxs: "h-5 w-5",
            xs: "h-7 w-7",
            sm: "h-9 w-9",
            md: "h-12 w-12",
            lg: "h-16 w-16",
            xl: "h-20 w-20",
            "2xl": "h-36 w-36",
            auto: "w-full relative",
        },
        variant: {
            default: "",
            clickable: "cursor-pointer hover:filter group-hover:filter group-hover:brightness-110 hover:brightness-110 group-active:brightness-90 active:brightness-90 transition duration-200 ease-out",
            disabled: "opacity-50",
        },
        rounded: {
            true: "rounded-full ring-[1px] ring-border-dark/50",
            false: "",
        },
    },
    compoundVariants: [
        {
            rounded: false,
            size: "xxs",
            className: "rounded",
        },
        {
            rounded: false,
            size: "xs",
            className: "rounded-md",
        },
        {
            rounded: false,
            size: "sm",
            className: "rounded-lg",
        },
        {
            rounded: false,
            size: "md",
            className: "rounded-xl",
        },
        {
            rounded: false,
            size: "lg",
            className: "rounded-2xl",
        },
        {
            rounded: false,
            size: "xl",
            className: "rounded-[22px]",
        },
        {
            rounded: false,
            size: "2xl",
            className: "rounded-[32px]",
        },
        {
            rounded: false,
            size: "auto",
            className: "rounded-[24%]",
        },
    ],
    defaultVariants: {
        size: "md",
        variant: "default",
        rounded: false,
    },
});
const textVariants = cva("select-none font-semibold", {
    variants: {
        size: {
            xxs: "text-[10px]",
            xs: "text-xs",
            sm: "text-sm",
            md: "text-base",
            lg: "text-3xl",
            xl: "text-5xl",
            "2xl": "text-7xl",
            auto: "text-xl",
        },
    },
    defaultVariants: {
        size: "md",
    },
});
const getColor = (name) => {
    if (/\+/.test(name)) {
        return "bg-primary-300";
    }
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = [
        "bg-blue-300",
        "bg-violet-300",
        "bg-pink-300",
        "bg-red-300",
        "bg-orange-300",
        "bg-golden-300",
        "bg-lime-300",
        "bg-emerald-300",
    ];
    return colors[Math.abs(hash) % colors.length];
};
const getTextVariant = (name) => {
    if (/\+/.test(name)) {
        return "text-muted-foreground";
    }
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const txtColors = [
        "text-blue-700",
        "text-violet-700",
        "text-pink-700",
        "text-red-700",
        "text-orange-700",
        "text-golden-700",
        "text-lime-700",
        "text-emerald-700",
    ];
    return txtColors[Math.abs(hash) % txtColors.length];
};
export function Avatar({ size, name, emoji, visual, onClick, clickable = false, busy = false, isRounded = false, backgroundColor, hexBgColor, disabled = false, className, icon, iconColor = "text-foreground", }) {
    const normalizedVisual = visual === "" ? null : visual;
    const emojiInfos = typeof normalizedVisual === "string" &&
        getEmojiAndBackgroundFromUrl(normalizedVisual);
    const backgroundColorToUse = emojiInfos
        ? emojiInfos.backgroundColor
        : backgroundColor;
    const emojiToUse = emojiInfos ? emojiInfos.skinEmoji : emoji;
    const visualToUse = emojiInfos ? null : normalizedVisual;
    const variant = disabled
        ? "disabled"
        : (onClick || clickable) && !busy
            ? "clickable"
            : "default";
    const isImageVisual = typeof visualToUse === "string";
    const bgColorClass = isImageVisual || hexBgColor
        ? ""
        : (backgroundColorToUse !== null && backgroundColorToUse !== void 0 ? backgroundColorToUse : (name ? getColor(name) : "bg-muted-background"));
    return (React.createElement("div", { className: cn(!isImageVisual && "border border-primary-800/10", avatarVariants({
            size,
            variant,
            rounded: isRounded,
        }), busy ? "animate-breathing cursor-default" : "", bgColorClass, className), style: hexBgColor && !isImageVisual
            ? { backgroundColor: hexBgColor }
            : undefined },
        size === "auto" && React.createElement("div", { style: { paddingBottom: "100%" } }),
        typeof visualToUse === "string" ? (React.createElement(ImageWrapper, { src: visualToUse, alt: name, className: cn(avatarVariants({ size }), "object-cover object-center") })) : visualToUse ? (visualToUse) : icon ? (React.createElement(icon, {
            className: cn("h-1/2 w-1/2", iconColor),
        })) : emojiToUse ? (React.createElement("span", { className: textVariants({ size }) }, emojiToUse)) : name ? (React.createElement("span", { className: cn(textVariants({ size }), getTextVariant(name)) }, /\+/.test(name) ? name : name[0].toUpperCase())) : (React.createElement(User01, { className: "h-1/2 w-1/2 text-foreground opacity-20" }))));
}
const AVATAR_STACK_SIZES = ["xs", "sm", "md"];
const sizeClassesPx = {
    xs: 24,
    sm: 32,
    md: 40,
};
Avatar.Stack = function ({ avatars, nbVisibleItems, size = "sm", hasMagnifier = true, tooltipTriggerAsChild = false, orientation = "horizontal", onTop = "last", }) {
    const [isHovered, setIsHovered] = useState(false);
    // Get visible avatars and calculate remaining count
    const shouldShowAll = !nbVisibleItems || avatars.length <= nbVisibleItems;
    const isFirstOnTop = onTop === "first";
    const maxVisible = shouldShowAll
        ? avatars.length
        : isFirstOnTop
            ? nbVisibleItems
            : nbVisibleItems - 1;
    const visibleAvatars = isFirstOnTop
        ? avatars.slice(0, maxVisible).reverse()
        : avatars.slice(0, maxVisible);
    const remainingCount = shouldShowAll || isFirstOnTop ? 0 : avatars.length - maxVisible;
    // Get all names for tooltip
    const avatarNames = avatars
        .filter((avatar) => avatar.name)
        .map((avatar) => avatar.name);
    const tooltipLabel = avatarNames.join(", ");
    const sizeSetting = {
        marginLeft: 0,
        widthHovered: sizeClassesPx[size] * 0.6,
        width: sizeClassesPx[size] * 0.25,
        heightHovered: sizeClassesPx[size] * 0.6,
        height: sizeClassesPx[size] * 0.25,
    };
    const collapsedWidth = sizeSetting.width *
        (visibleAvatars.length + Number(Boolean(remainingCount))) +
        (sizeClassesPx[size] - sizeSetting.width);
    const openedWidth = sizeSetting.widthHovered *
        (visibleAvatars.length + Number(Boolean(remainingCount))) +
        (sizeClassesPx[size] - sizeSetting.widthHovered);
    const collapsedHeight = sizeSetting.height *
        (visibleAvatars.length + Number(Boolean(remainingCount))) +
        (sizeClassesPx[size] - sizeSetting.height);
    const openedHeight = sizeSetting.heightHovered *
        (visibleAvatars.length + Number(Boolean(remainingCount))) +
        (sizeClassesPx[size] - sizeSetting.heightHovered);
    const transitionSettings = orientation === "vertical"
        ? "height 200ms ease-out"
        : "width 200ms ease-out";
    return (React.createElement(Tooltip, { label: tooltipLabel, tooltipTriggerAsChild: tooltipTriggerAsChild, trigger: React.createElement(React.Fragment, null,
            React.createElement("div", { className: cn("flex", onTop === "first"
                    ? orientation === "vertical"
                        ? "flex-col-reverse justify-end"
                        : "flex-row-reverse justify-end"
                    : orientation === "vertical"
                        ? "flex-col"
                        : "flex-row"), onMouseEnter: () => visibleAvatars.length > 1 && setIsHovered(true), onMouseLeave: () => visibleAvatars.length > 1 && setIsHovered(false), style: {
                    [orientation === "vertical" ? "height" : "width"]: `${isHovered
                        ? orientation === "vertical"
                            ? openedHeight
                            : openedWidth
                        : orientation === "vertical"
                            ? collapsedHeight
                            : collapsedWidth}px`,
                    transition: transitionSettings,
                } },
                visibleAvatars.map((avatarProps, i) => (React.createElement("div", { key: i, className: "cursor-pointer", style: {
                        [orientation === "vertical" ? "height" : "width"]: isHovered
                            ? orientation === "vertical"
                                ? sizeSetting.heightHovered
                                : sizeSetting.widthHovered
                            : orientation === "vertical"
                                ? sizeSetting.height
                                : sizeSetting.width,
                        transition: transitionSettings,
                    } }, hasMagnifier ? (React.createElement("div", { style: {
                        transform: `scale(${onTop === "first"
                            ? 1 -
                                (visibleAvatars.length +
                                    (remainingCount > 0 ? 1 : 0) -
                                    1 -
                                    i) *
                                    0.06
                            : 1 -
                                (visibleAvatars.length +
                                    (remainingCount > 0 ? 1 : 0) -
                                    i) *
                                    0.06})`,
                    } },
                    React.createElement(Avatar, { ...avatarProps, size: size }))) : (React.createElement(Avatar, { ...avatarProps, size: size }))))),
                remainingCount > 0 && (React.createElement("div", { className: "cursor-pointer", style: {
                        [orientation === "vertical" ? "height" : "width"]: isHovered
                            ? orientation === "vertical"
                                ? sizeSetting.heightHovered
                                : sizeSetting.widthHovered
                            : orientation === "vertical"
                                ? sizeSetting.height
                                : sizeSetting.width,
                        transition: transitionSettings,
                    } }, hasMagnifier ? (React.createElement("div", { style: {
                        transform: `scale(${onTop === "first" ? 1 : 1 - 0.06})`,
                    } },
                    React.createElement(Avatar, { size: size, name: Number(remainingCount) < 10
                            ? `+${remainingCount}`
                            : "9+" }))) : (React.createElement(Avatar, { size: size, name: Number(remainingCount) < 10 ? `+${remainingCount}` : "9+" })))))) }));
};
//# sourceMappingURL=Avatar.js.map